/* eslint-disable security/detect-object-injection, @nfq/no-magic-numbers, @typescript-eslint/no-empty-object-type */
type KeyframeValue = number | string;

/**
 * Represents a single keyframe step in an animation sequence.
 * Each step defines the point in time within the animation (in milliseconds or another
 * time unit) and the corresponding value to apply at that moment. This structure is
 * useful for building animations or transitions where values change over time.
 */
type Step = {
    /**
     * The time marker (e.g., in milliseconds) indicating when this step occurs in the animation.
     */
    at: number;
    /**
     * The value to be applied at the specified time marker in the animation sequence.
     */
    value: KeyframeValue;
};


/**
 * Represents a property animation configuration containing all steps and the final time marker.
 * This type groups multiple `Step` entries together along with the `lastAt` value, which defines
 * the end time of the animation. It can be used for calculating interpolations or for playback
 * in a timeline-based animation system.
 */
type Prop = {
    /**
     * The timestamp (e.g., in milliseconds) indicating the end of the animation sequence.
     */
    lastAt: number;
    /**
     * An ordered list of steps defining value changes throughout the animation.
     */
    steps: Step[];
};

/**
 * A builder utility for composing synchronized transition keyframes across multiple properties.
 * This class collects time-stamped value steps for each animated property and compiles them into
 * normalized keyframe arrays consumable by animation libraries. It maintains a per-property timeline
 * (`lastAt`) to simplify sequencing operations like pauses and value transitions. The resulting structure
 * ensures that all properties share the same time axis for consistent multi-property animations.
 *
 * @example
 * ```tsx
 * // Create a builder and register two properties with initial values
 * const builder = new TransitionKeyframeBuilder()
 *   .addProperty('opacity', 0)
 *   .addProperty('x', 0);
 *
 * // Sequence: hold, then animate to new values
 * builder.pause('opacity', 200).to('opacity', 1, 300);
 * builder.to('x', 50, 150).pause('x', 100).to('x', 100, 250);
 *
 * // Compile into normalized times [0..1] and per-prop value arrays
 * const { times, values } = builder.compile();
 * // times: number[]; values: { opacity: KeyframeValue[]; x: KeyframeValue[] }
 * ```
 */
export class TransitionKeyframeBuilder<Items extends Record<string, Prop> = {}> {
    /**
     * The map of animated properties and their accumulated steps.
     * Each entry stores the property's timeline (`lastAt`) and an ordered list of `steps` representing
     * value changes over time. Keys correspond to property names, while values conform to the `Prop` type.
     */
    private properties: Items = {} as Items;

    /**
     * Compiles all registered property timelines into normalized, aligned keyframe arrays.
     * This method first computes the union of all timestamps across every property to produce a common time
     * axis. It then normalizes these timestamps to a `[0, 1]` range based on the largest absolute time point.
     * For each property, it fills the value array by selecting the most recent step at or before each time
     * on the common axis, ensuring alignment across properties.
     *
     * @returns An object containing `times` (normalized array shared by all properties) and `values` (a record mapping each property to its corresponding keyframe value array).
     *
     * @example
     * ```tsx
     * const { times, values } = builder.compile();
     * // Use with an animation system: animate(times, values.opacity, values.x)
     * ```
     */
    compile() {
        const allTimes = Array.from(
            new Set(Object.values(this.properties).flatMap(b => b.steps.map(s => s.at)))
        ).sort((a, b) => a - b);
        const maxTime = allTimes[allTimes.length - 1] || 1;
        const normalizedTimes = allTimes.map(t => t / maxTime);
        const result: Record<string, KeyframeValue[]> = {};

        for (const [prop, builder] of Object.entries(this.properties)) {
            const {steps} = builder;
            const values: KeyframeValue[] = [];

            let idx = 0;

            for (const t of allTimes) {
                // Find the last step at or before t
                while (idx + 1 < steps.length && steps[idx + 1].at <= t) idx++;
                values.push(steps[idx].value);
            }

            result[prop] = values;
        }

        return {
            times: normalizedTimes,
            ...(result as Record<keyof Items, KeyframeValue[]>)
        };
    }

    /**
     * Registers a new animated property with its initial value at time `0`.
     * This method initializes the property's timeline with `lastAt = 0` and seeds the steps list with the
     * starting value. It returns a widened builder type so subsequent calls can reference the newly added key.
     * Use this prior to sequencing pauses or transitions for the property.
     *
     * @param name  The new property's name to be added to the builder.
     * @param value The initial value assigned at time `0` for the property.
     * @returns The builder instance with an updated `Items` type including the new property.
     *
     * @example
     * ```tsx
     * const b = new TransitionKeyframeBuilder()
     *   .addProperty('scale', 1)
     *   .to('scale', 1.2, 150);
     * ```
     */
    addProperty<NewItemKey extends string>(name: NewItemKey, value: KeyframeValue) {
        this.properties = {
            ...this.properties,
            [name]: {
                lastAt: 0,
                steps: [{
                    at: 0,
                    value
                }]
            } as Prop
        };

        return this as TransitionKeyframeBuilder<Items & Record<NewItemKey, Prop>>;
    }

    /**
     * Inserts a time span where the property's value is held constant (no change).
     * This updates the property's timeline by advancing `lastAt` by the given `span` and appending a step
     * with the most recent value. It is useful for delaying subsequent transitions while maintaining the
     * current state.
     *
     * @param prop The property to pause.
     * @param span The duration to hold the current value before the next change (in the same time unit as steps).
     * @returns The builder instance for chaining further operations.
     *
     * @example
     * ```tsx
     * builder.pause('opacity', 200).to('opacity', 1, 300);
     * ```
     */
    pause(prop: keyof Items, span: number) {
        this.properties[prop].lastAt += span;
        this.properties[prop].steps.push({
            at: this.properties[prop].lastAt,
            value: this.properties[prop].steps.at(-1)!.value
        });

        return this;
    }

    /**
     * Appends a transition step that sets the property to a new value after a time span.
     * This advances the property's `lastAt` by the provided `span` and records the new `value` at that time.
     * Use this to build sequential animations where each step occurs after the previous one completes.
     *
     * @param prop  The property being animated.
     * @param value The value to set at the end of the span.
     * @param span  The duration after which the new value should take effect.
     * @returns The builder instance for chaining.
     *
     * @example
     * ```tsx
     * builder.to('x', 100, 250).to('x', 0, 250);
     * ```
     */
    to(prop: keyof Items, value: KeyframeValue, span: number) {
        this.properties[prop].lastAt += span;
        this.properties[prop].steps.push({
            at: this.properties[prop].lastAt,
            value
        });

        return this;
    }

    /**
     * Sets the property to a new value at an absolute timestamp, inserting a hold if needed.
     * This method jumps the property's timeline to a specific moment `at`, appends a step that keeps the
     * last value up to that time for continuity, and then adds a step with the new `value`. It is useful
     * when coordinating properties with absolute timelines or aligning to external cues.
     *
     * @param prop  The property being updated at an absolute time.
     * @param value The value to set at the specified timestamp.
     * @param at    The absolute time at which to apply the new value.
     * @returns The builder instance for chaining.
     *
     * @example
     * ```tsx
     * builder.setAt('opacity', 0.5, 120).setAt('opacity', 1, 240);
     * ```
     */
    setAt(prop: keyof Items, value: KeyframeValue, at: number) {
        this.properties[prop].lastAt = at;
        this.properties[prop].steps.push({
            at: this.properties[prop].lastAt,
            value: this.properties[prop].steps.at(-1)!.value
        });
        this.properties[prop].steps.push({
            at: this.properties[prop].lastAt,
            value
        });

        return this;
    }
}